import { Link } from "react-router-dom";
import { Book, Users, Zap, ListChecks, HelpCircle, ArrowRight } from "lucide-react";
import MarketingHeader from "@/components/marketing/MarketingHeader";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageTransition } from "@/components/PageTransition";
import { FieldGuideSearch } from "@/components/fieldguide/FieldGuideSearch";

const FieldGuide = () => {
  return (
    <PageTransition>
      <div className="min-h-screen bg-background">
        <MarketingHeader />

        {/* Hero Section */}
        <section className="border-b border-border py-16 md:py-24">
          <div className="container mx-auto px-6">
            <div className="mx-auto max-w-3xl text-center">
              <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-2 text-sm font-medium text-primary">
                <Book className="h-4 w-4" />
                PROduce Field Guide
              </div>
              <h1 className="mb-6 text-4xl font-bold md:text-5xl">
                Everything you need to know about PROduce
              </h1>
              <p className="mb-8 text-xl text-muted-foreground">
                A complete reference guide for producers and crew members. Learn how to set up
                productions, manage crews, and coordinate live events.
              </p>

              {/* Working Search */}
              <FieldGuideSearch />
            </div>
          </div>
        </section>

        {/* Main Guides */}
        <section className="py-16">
          <div className="container mx-auto px-6">
            <div className="mx-auto max-w-5xl">
              <h2 className="mb-8 text-center text-2xl font-semibold">Choose your guide</h2>

              <div className="mb-16 grid gap-6 md:grid-cols-2">
                {/* Producer Guide Card */}
                <Card className="group border-border bg-card transition-colors hover:border-primary/50">
                  <CardHeader>
                    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                      <Users className="h-6 w-6 text-primary" />
                    </div>
                    <CardTitle className="text-2xl">Producer Guide</CardTitle>
                    <CardDescription className="text-base">
                      Learn how to create productions, manage crews, build schedules, and track your
                      team during live events.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="mb-6 space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-center gap-2">
                        <ArrowRight className="h-4 w-4 text-primary" />
                        Production setup (9-step wizard)
                      </li>
                      <li className="flex items-center gap-2">
                        <ArrowRight className="h-4 w-4 text-primary" />
                        Crew management & invitations
                      </li>
                      <li className="flex items-center gap-2">
                        <ArrowRight className="h-4 w-4 text-primary" />
                        Schedule building & exports
                      </li>
                      <li className="flex items-center gap-2">
                        <ArrowRight className="h-4 w-4 text-primary" />
                        Live tracking & analytics
                      </li>
                    </ul>
                    <Link to="/field-guide/producer">
                      <Button className="w-full group-hover:bg-primary group-hover:text-primary-foreground">
                        Open Producer Guide
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>

                {/* Crew Guide Card */}
                <Card className="group border-border bg-card transition-colors hover:border-secondary/50">
                  <CardHeader>
                    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-secondary/10">
                      <Zap className="h-6 w-6 text-secondary" />
                    </div>
                    <CardTitle className="text-2xl">Crew member guide</CardTitle>
                    <CardDescription className="text-base">
                      Learn how to use the crew app, view your schedule, navigate the event, and
                      stay connected with your team.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="mb-6 space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-center gap-2">
                        <ArrowRight className="h-4 w-4 text-secondary" />
                        Getting started & app install
                      </li>
                      <li className="flex items-center gap-2">
                        <ArrowRight className="h-4 w-4 text-secondary" />
                        Your schedule & assignments
                      </li>
                      <li className="flex items-center gap-2">
                        <ArrowRight className="h-4 w-4 text-secondary" />
                        Briefing & event information
                      </li>
                      <li className="flex items-center gap-2">
                        <ArrowRight className="h-4 w-4 text-secondary" />
                        Live tracking & offline mode
                      </li>
                    </ul>
                    <Link to="/field-guide/crew">
                      <Button
                        variant="outline"
                        className="w-full group-hover:border-secondary group-hover:text-secondary"
                      >
                        Open Crew Guide
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </div>

              {/* Quick Links */}
              <div className="border-t border-border pt-12">
                <h2 className="mb-8 text-center text-2xl font-semibold">Quick access</h2>

                <div className="grid gap-4 sm:grid-cols-2">
                  <Link to="/field-guide/quick-reference">
                    <Card className="cursor-pointer border-border bg-card/50 transition-colors hover:bg-card">
                      <CardContent className="flex items-center gap-4 p-6">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10">
                          <ListChecks className="h-5 w-5 text-accent" />
                        </div>
                        <div>
                          <h3 className="font-semibold">Quick Reference</h3>
                          <p className="text-sm text-muted-foreground">
                            Status meanings, shortcuts, and quick lookups
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>

                  <Link to="/field-guide/troubleshooting">
                    <Card className="cursor-pointer border-border bg-card/50 transition-colors hover:bg-card">
                      <CardContent className="flex items-center gap-4 p-6">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-destructive/10">
                          <HelpCircle className="h-5 w-5 text-destructive" />
                        </div>
                        <div>
                          <h3 className="font-semibold">Troubleshooting</h3>
                          <p className="text-sm text-muted-foreground">
                            Common issues and how to resolve them
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-border py-8">
          <div className="container mx-auto px-6">
            <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
              <p className="text-sm text-muted-foreground">
                PROduce Field Guide — Last updated January 2026
              </p>
              <div className="flex items-center gap-6">
                <Link
                  to="/privacy-policy"
                  className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  Privacy Policy
                </Link>
                <Link
                  to="/contact"
                  className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  Contact
                </Link>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </PageTransition>
  );
};

export default FieldGuide;
